import { Icon } from "@/components/Icon";
import { formatMoney } from "@/lib/format";
import "./InsightCard.css";

/**
 * The AI insight strip.
 *
 * WAS static demo copy — "Fuel spend is up 18% against last August — and 3
 * receipts are still uncategorized" — with neither number computed from
 * anything. That is not merely unhelpful, it actively CONTRADICTED the
 * Capture screen (which showed "No receipts captured yet" while this banner
 * claimed 3 were sitting uncategorized): the demo's most prominent
 * gold-wash "the software went looking" banner disagreeing with the one
 * screen a prospect would check it against.
 *
 * `uncategorizedCount` fixes the half that's honestly derivable: it is
 * `entries.filter(e => e.source === "Receipt" && !e.accountId).length` —
 * the SAME definition Capture.tsx's own feed and EntryRow.tsx's
 * `sourceLabel` ("Scanned" vs "Auto-categorized") already use, computed once
 * in Dashboard.tsx from the live `useEntries` feed it already holds. Live
 * question, live answer, live update as receipts get scanned or corrected —
 * not a second source of truth.
 *
 * The fuel-spend comparison is GONE, not relabeled. "Up 18% against last
 * August" needs a same-account figure from a full prior August to compare
 * against; the seeded ledger (db/seed/0002_seed_demo.sql) is 14 entries
 * spanning about eight days, and nothing in this API surface exposes prior
 * periods at all. There is no honest way to print that sentence today. A
 * card that says less but only says true things beats one that always has
 * something clever to say — see the DEMO_KPIS reasoning in Dashboard.tsx
 * for the same trade-off made twice in one screen. If a real multi-period
 * endpoint ever exists, that clause can come back computed from it; it must
 * not come back as a constant.
 */
interface InsightCardProps {
  /** Count of `source === "Receipt"` entries with no confirmed account —
   *  see the class doc for exactly why this definition, not another one. */
  uncategorizedCount: number;
  /**
   * The sum of those receipts. Turns a named state into its consequence — see Dashboard.tsx's
   * own comment for the measurement, and why a "Review →" button was the wrong fix.
   */
  uncategorizedTotal: number;
  /** Present only when there's somewhere to send "Review →" — the Capture
   *  screen, where the uncategorized receipts this insight names actually
   *  live. Omitted entirely rather than rendered disabled: a card with
   *  nowhere to go should not offer a control that does nothing. */
  onReview?: () => void;
}

export function InsightCard({
  uncategorizedCount,
  uncategorizedTotal,
  onReview,
}: InsightCardProps) {
  return (
    <aside className="insight" aria-label="Insight">
      {/* Drawn, not ✨ — see components/Icon.tsx. The emoji rendered as a flat
          grey asterisk on the booth laptop, which is worse than no icon. */}
      <Icon name="sparkle" size={19} className="insight__icon" />
      {/* aria-live: this sentence changes as receipts are scanned or
          corrected on the SAME live feed the dashboard already renders — a
          screen-reader user should hear that the count moved, the same
          reasoning as KpiCard's own live value row. */}
      <p className="insight__text" aria-live="polite">
        {uncategorizedCount > 0 ? (
          <>
            <strong>{uncategorizedCount}</strong>{" "}
            {uncategorizedCount === 1 ? "receipt is" : "receipts are"} still
            uncategorized, so {uncategorizedCount === 1 ? "its" : "their"}{" "}
            {/* "is", never "are": the verb agrees with the MONEY, which is one sum however
                many receipts it came from. It was tied to the receipt count and read "their
                $1,412.96 are in the activity feed" the first time three of them piled up —
                a case the wall's single receipt could never have shown. */}
            {formatMoney(uncategorizedTotal)} is in the activity feed and not in the totals
            yet.
          </>
        ) : (
          "No uncategorized receipts right now."
        )}
        {onReview && (
          <button type="button" className="insight__review" onClick={onReview}>
            Review<span aria-hidden="true"> →</span>
          </button>
        )}
      </p>
    </aside>
  );
}
